/**
 * BL-194 Gate 1 — Module 5 Dashboard data layer (client side).
 *
 * Locked design: IntelAgent_BL194_Module5_Dashboard_Phase1_RemainingBuild_v1_01.
 * Dashboard reads Supabase directly — the views in migration 092 are declared
 * security_invoker, so the caller's own RLS decides which rows the aggregate
 * sees. Nothing here filters by org in JavaScript: doing so would be theatre,
 * the database already refuses the rows.
 *
 * The fetch functions are thin. Everything shaped or derived lives in an
 * exported pure function so it can be tested without a database.
 */

import { supabaseClient } from '../config/supabase';
import { windowStartDate } from './dashboardMetricsCore.js';

export * from './dashboardMetricsCore.js';

const ENGAGEMENT_PRICE_BATCH_SIZE = 500;
const ENGAGEMENT_QUERY_PAGE_SIZE = 1000;

async function fetchEngagementPrices(engagementIds) {
    const pricesByEngagement = new Map();
    for (let offset = 0; offset < engagementIds.length; offset += ENGAGEMENT_PRICE_BATCH_SIZE) {
        const ids = engagementIds.slice(offset, offset + ENGAGEMENT_PRICE_BATCH_SIZE);
        const { data, error } = await supabaseClient.rpc('bl395a_engagement_prices', {
            p_engagement_ids: ids,
        });
        if (error) throw error;
        (data || []).forEach((price) => pricesByEngagement.set(price.engagement_id, price));
        ids.forEach((id) => {
            if (!pricesByEngagement.has(id)) {
                throw new Error(`Fare is unavailable for engagement ${id}.`);
            }
        });
    }
    return pricesByEngagement;
}

async function fetchRecentEngagements(startDate, { excludeTestTrips = false } = {}) {
    const engagements = [];
    for (let offset = 0; ; offset += ENGAGEMENT_QUERY_PAGE_SIZE) {
        // Breaker Panel: once Dispatch is Live, TEST trips stay out of the fares (unless the person chose Include TEST).
        let query = supabaseClient
            .from('engagements')
            .select('id, created_at');
        if (excludeTestTrips) query = query.eq('is_test', false);
        const { data, error } = await query
            .gte('created_at', startDate)
            .not('status', 'in', '(canceled,no_show)')
            .order('id', { ascending: true })
            .range(offset, offset + ENGAGEMENT_QUERY_PAGE_SIZE - 1);
        if (error) throw error;
        const page = data || [];
        engagements.push(...page);
        if (page.length < ENGAGEMENT_QUERY_PAGE_SIZE) break;
    }
    return engagements;
}

/** Read the org's headline KPI row. Returns null when the tenant has no row yet. */
export async function fetchKpiRollup() {
    const { data, error } = await supabaseClient
        .from('dashboard_kpi_rollup')
        .select('*')
        .maybeSingle();
    if (error) throw error;
    return data || null;
}

/** Read daily invoiced/collected buckets for the requested window. */
export async function fetchRevenueTrend(timeframe = '30d', today = new Date()) {
    const { data, error } = await supabaseClient
        .from('dashboard_revenue_trend')
        .select('bucket_date, invoiced_amount, collected_amount, invoice_count, payment_count')
        .gte('bucket_date', windowStartDate(timeframe, today))
        .order('bucket_date', { ascending: true });
    if (error) throw error;
    return data || [];
}

/** Read the trailing-window rate of change. */
export async function fetchVelocity() {
    const { data, error } = await supabaseClient
        .from('dashboard_velocity')
        .select('*')
        .maybeSingle();
    if (error) throw error;
    return data || null;
}

/** Read the weekday x hour booking-demand grid and add fares through the protected RPC. */
export async function fetchDeadZones({ showTripPrices = false, now = new Date(), excludeTestTrips = false } = {}) {
    const { data, error } = await supabaseClient
        .from('dashboard_dead_zones')
        .select('day_of_week, hour_of_day, engagement_count, engagement_amount, is_dead_zone');
    if (error) throw error;
    const rows = data || [];
    if (!showTripPrices) return rows;

    const startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString();
    const engagements = await fetchRecentEngagements(startDate, { excludeTestTrips });
    const pricesByEngagement = await fetchEngagementPrices(engagements.map((engagement) => engagement.id));
    const amountsBySlot = new Map();
    engagements.forEach((engagement) => {
        const createdAt = new Date(engagement.created_at);
        if (Number.isNaN(createdAt.getTime())) {
            throw new Error(`Creation date is invalid for engagement ${engagement.id}.`);
        }
        const key = `${createdAt.getUTCDay()}:${createdAt.getUTCHours()}`;
        const price = pricesByEngagement.get(engagement.id);
        const amount = Number(price.final_amount ?? price.quoted_amount ?? 0);
        amountsBySlot.set(key, (amountsBySlot.get(key) || 0) + amount);
    });

    return rows.map((row) => ({
        ...row,
        engagement_amount: amountsBySlot.get(`${row.day_of_week}:${row.hour_of_day}`) || 0,
    }));
}

/** Read active goals with their period-to-date attainment. */
export async function fetchGoalGap() {
    const { data, error } = await supabaseClient
        .from('dashboard_goal_gap')
        .select('*')
        .order('metric_key', { ascending: true });
    if (error) throw error;
    return data || [];
}

/** Read recent events, adding engagement fares only through the protected RPC. */
export async function fetchRecentActivity(limit = 20, { showTripPrices = false } = {}) {
    const { data, error } = await supabaseClient
        .from('dashboard_recent_activity')
        .select('activity_type, source_id, title, amount, status, occurred_at')
        .order('occurred_at', { ascending: false, nullsFirst: false })
        .limit(limit);
    if (error) throw error;
    const rows = data || [];
    const engagementRows = rows.filter((row) => row.activity_type === 'engagement');
    if (!showTripPrices || engagementRows.length === 0) return rows;

    const pricesByEngagement = await fetchEngagementPrices(engagementRows.map((row) => row.source_id));
    return rows.map((row) => {
        if (row.activity_type !== 'engagement') return row;
        const price = pricesByEngagement.get(row.source_id);
        return { ...row, amount: Number(price.final_amount ?? price.quoted_amount ?? 0) };
    });
}

/**
 * One round trip per tile-data view, in parallel.
 *
 * A single failing view must not blank the whole dashboard, so each result is
 * settled independently and the failures are reported alongside the data that
 * did load.
 */
export async function fetchDashboardMetrics({
    timeframe = '30d',
    activityLimit = 20,
    showTripPrices = false,
    excludeTestTrips = false,
} = {}) {
    const [rollup, revenueTrend, velocity, deadZones, goalGap, recentActivity] = await Promise.allSettled([
        fetchKpiRollup(),
        fetchRevenueTrend(timeframe),
        fetchVelocity(),
        fetchDeadZones({ showTripPrices, excludeTestTrips }),
        fetchGoalGap(),
        fetchRecentActivity(activityLimit, { showTripPrices }),
    ]);

    const errors = [];
    const unwrap = (result, name, fallback) => {
        if (result.status === 'fulfilled') return result.value;
        errors.push({ source: name, message: result.reason?.message || String(result.reason) });
        return fallback;
    };

    return {
        timeframe,
        rollup: unwrap(rollup, 'dashboard_kpi_rollup', null),
        revenueTrend: unwrap(revenueTrend, 'dashboard_revenue_trend', []),
        velocity: unwrap(velocity, 'dashboard_velocity', null),
        deadZones: unwrap(deadZones, 'dashboard_dead_zones', []),
        goalGap: unwrap(goalGap, 'dashboard_goal_gap', []),
        recentActivity: unwrap(recentActivity, 'dashboard_recent_activity', []),
        errors,
    };
}
